import { useEffect, useState, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
    getCompanyUsers,
    getCompanyAnalytics,
    calculateJobStats,
    calculateUserStats,
    calculateDeliveryStats,
    calculateActivityStats,
    calculateSMSStats,
    getJobTrends,
    getActivityTrends,
    getPerformanceStats,
    getProductionStats
} from '../../lib/supabase/analytics';
import {
    FiBarChart2, FiBriefcase, FiUsers, FiTruck, FiActivity,
    FiMessageSquare, FiTrendingUp, FiClock, FiCheckCircle,
    FiAlertCircle, FiPackage, FiTarget, FiZap, FiAward,
    FiRefreshCw, FiCalendar, FiPieChart, FiLayers, FiFilter,
    FiEdit3, FiUser, FiDollarSign, FiChevronDown, FiX
} from 'react-icons/fi';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './analytics.css';
import {
    AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
    LineChart, Line, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { Crown } from 'lucide-react';

const Analytics = () => {
    const { company } = useAuth();
    const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 767px)').matches);

    useEffect(() => {
        const media = window.matchMedia('(max-width: 767px)');
        const update = () => setIsMobile(media.matches);
        media.addEventListener('change', update);
        return () => media.removeEventListener('change', update);
    }, []);

    const [initialLoading, setInitialLoading] = useState(true);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [performanceLoading, setPerformanceLoading] = useState(false);
    const [productionLoading, setProductionLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const [pendingAnalyticsData, setPendingAnalyticsData] = useState(null);
    const [loadedSections, setLoadedSections] = useState({});

    const [lastUpdated, setLastUpdated] = useState(null);

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState('all');
    const [userDropdownOpen, setUserDropdownOpen] = useState(false);

    const [designerSortMode, setDesignerSortMode] = useState('done');
    const [estimatorSortMode, setEstimatorSortMode] = useState('done');
    const [expandedLeaderboards, setExpandedLeaderboards] = useState({});

    const [analyticsData, setAnalyticsData] = useState({
        jobs: [],
        users: [],
        actionLogs: [],
        deliveries: [],
        smsLogs: [],
    });

    const [performanceStats, setPerformanceStats] = useState(null);
    const [productionStats, setProductionStats] = useState(null);

    const filters = useMemo(() => ({
        startDate,
        endDate,
        selectedUserId,
    }), [startDate, endDate, selectedUserId]);

    const fetchUsersOnly = useCallback(async () => {
        if (!company?.id) return;

        try {
            const users = await getCompanyUsers(company.id);

            setAnalyticsData(prev => ({
                ...prev,
                users: users || [],
            }));

            setPendingAnalyticsData(prev => prev
                ? { ...prev, users: users || [] }
                : prev
            );
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    }, [company?.id]);

    const fetchCoreAnalytics = useCallback(async (isRefresh = false) => {
        if (!company?.id) return;

        try {
            if (isRefresh) setRefreshing(true);

            setAnalyticsLoading(true);
            setLoadedSections({});
            setPendingAnalyticsData({
                jobs: [],
                users: analyticsData.users || [],
                actionLogs: [],
                deliveries: [],
                smsLogs: [],
            });

            const nextPartial = {
                jobs: [],
                users: analyticsData.users || [],
                actionLogs: [],
                deliveries: [],
                smsLogs: [],
            };

            const data = await getCompanyAnalytics(company.id, filters, {
                onSectionLoaded: (section, rows) => {
                    nextPartial[section] = rows || [];

                    setPendingAnalyticsData(prev => ({
                        ...(prev || nextPartial),
                        [section]: rows || [],
                    }));

                    setLoadedSections(prev => ({
                        ...prev,
                        [section]: true,
                    }));

                    setInitialLoading(false);
                },
            });

            // Important: charts only receive ONE final update here.
            setAnalyticsData(data);
            setPendingAnalyticsData(null);
            setLoadedSections({});
            setLastUpdated(new Date());
        } catch (error) {
            console.error('Error fetching analytics:', error);
        } finally {
            setAnalyticsLoading(false);
            setInitialLoading(false);
            setRefreshing(false);
        }
    }, [company?.id, filters, analyticsData.users]);

    const fetchPerformance = useCallback(async () => {
        if (!company?.id) return;

        try {
            setPerformanceLoading(true);
            const performance = await getPerformanceStats(company.id, filters);
            setPerformanceStats(performance);
        } catch (error) {
            console.error('Error fetching performance stats:', error);
        } finally {
            setPerformanceLoading(false);
        }
    }, [company?.id, filters]);

    const fetchProduction = useCallback(async () => {
        if (!company?.id) return;

        try {
            setProductionLoading(true);
            const production = await getProductionStats(company.id, filters);
            setProductionStats(production);
        } catch (error) {
            console.error('Error fetching production stats:', error);
        } finally {
            setProductionLoading(false);
        }
    }, [company?.id, filters]);

    useEffect(() => {
        fetchUsersOnly();
    }, [fetchUsersOnly]);

    useEffect(() => {
        if (!company?.id) return;

        const hasAnyData =
            analyticsData.jobs.length ||
            analyticsData.users.length ||
            analyticsData.actionLogs.length ||
            analyticsData.deliveries.length ||
            analyticsData.smsLogs.length;

        setInitialLoading(!hasAnyData);

        fetchCoreAnalytics(false);
        fetchPerformance();
        fetchProduction();
    }, [company?.id, filters]);

    const handleRefresh = async () => {
        await Promise.all([
            fetchCoreAnalytics(true),
            fetchPerformance(),
            fetchProduction(),
        ]);
    };

    const jobStats = useMemo(() => calculateJobStats(analyticsData.jobs), [analyticsData.jobs]);
    const userStats = useMemo(() => calculateUserStats(analyticsData.users), [analyticsData.users]);
    const deliveryStats = useMemo(() => calculateDeliveryStats(analyticsData.deliveries), [analyticsData.deliveries]);
    const activityStats = useMemo(() => calculateActivityStats(analyticsData.actionLogs), [analyticsData.actionLogs]);
    const smsStats = useMemo(() => calculateSMSStats(analyticsData.smsLogs), [analyticsData.smsLogs]);

    const jobTrends = useMemo(() => getJobTrends(analyticsData.jobs), [analyticsData.jobs]);
    const activityTrends = useMemo(() => getActivityTrends(analyticsData.actionLogs), [analyticsData.actionLogs]);
    const latestLogs = useMemo(() => analyticsData.actionLogs.slice(0, 8), [analyticsData.actionLogs]);

    const clearDateFilter = () => {
        setStartDate(null);
        setEndDate(null);
    };

    const clearAllFilters = () => {
        setStartDate(null);
        setEndDate(null);
        setSelectedUserId('all');
    };

    const setQuickRange = (months) => {
        const end = new Date();
        end.setHours(23, 59, 59, 999);

        const start = new Date();
        start.setMonth(start.getMonth() - months);
        start.setHours(0, 0, 0, 0);

        setStartDate(start);
        setEndDate(end);
    };

    const handleDateChange = (dates) => {
        const [start, end] = dates;

        setStartDate(start);

        // Allows single-day selection. If user picks only one date,
        // backend receives startDate only and your getDateWindow should treat it as same-day.
        setEndDate(end);
    };

    const formatDateTime = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleString('en-AU', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDateLabel = () => {
        if (!startDate && !endDate) return 'All time';
        if (startDate && !endDate) return startDate.toLocaleDateString('en-AU');
        return `${startDate.toLocaleDateString('en-AU')} - ${endDate.toLocaleDateString('en-AU')}`;
    };

    const getUserDisplayName = (user) => {
        return user.full_name || user.username || user.email || 'Unnamed user';
    };

    const getActionIcon = (action) => {
        const iconMap = {
            job_created: FiCheckCircle,
            job_updated: FiEdit3,
            'Job Updated': FiEdit3,
            job_assigned: FiUser,
            job_completed: FiCheckCircle,
            status_changed: FiClock,
            designer_assigned: FiUser,
            removed_assignee: FiUser,
            estimator_assigned: FiDollarSign,
            production_started: FiPackage,
            delivery_scheduled: FiTruck,
        };

        return iconMap[action] || FiActivity;
    };

    const getActionColor = (action) => {
        const colorMap = {
            job_created: 'text-emerald-700 bg-emerald-50 border-emerald-100',
            job_updated: 'text-blue-700 bg-blue-50 border-blue-100',
            job_assigned: 'text-purple-700 bg-purple-50 border-purple-100',
            job_completed: 'text-emerald-700 bg-emerald-50 border-emerald-100',
            status_changed: 'text-amber-700 bg-amber-50 border-amber-100',
            designer_assigned: 'text-indigo-700 bg-indigo-50 border-indigo-100',
            estimator_assigned: 'text-teal-700 bg-teal-50 border-teal-100',
            production_started: 'text-orange-700 bg-orange-50 border-orange-100',
            delivery_scheduled: 'text-pink-700 bg-pink-50 border-pink-100',
        };

        return colorMap[action] || 'text-zinc-700 bg-zinc-50 border-zinc-200';
    };

    const parseLogDetails = (details) => {
        if (!details) return 'No details';

        if (typeof details === 'string') {
            try {
                const parsed = JSON.parse(details);
                return parsed.message || parsed.details || details;
            } catch {
                return details;
            }
        }

        if (typeof details === 'object') {
            return details.message || details.details || JSON.stringify(details);
        }

        return String(details);
    };

    const COLORS = {
        black: '#111111',
        zinc: '#71717A',
        blue: '#2563EB',
        green: '#059669',
        purple: '#7C3AED',
        amber: '#D97706',
        red: '#DC2626',
        indigo: '#4F46E5',
        pink: '#DB2777',
        teal: '#0D9488',
        orange: '#EA580C',
        emerald: '#059669'
    };

    const STATUS_COLORS = [
        '#111111',
        '#2563EB',
        '#059669',
        '#7C3AED',
        '#D97706',
        '#DC2626',
        '#4F46E5',
        '#0D9488',
        '#EA580C',
        '#71717A'
    ];

    const allJobStatuses = [
        'pending_design',
        'in_design',
        'pending_estimation',
        'in_estimation',
        'pending_production',
        'in_production',
        'pending_delivery',
        'in_delivery',
        'completed'
    ];

    const prettyLabel = (value) => {
        if (!value) return 'Unknown';
        return value
            .replace(/_/g, ' ')
            .split(' ')
            .map(w => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' ');
    };

    const jobStatusData = allJobStatuses.map((status, index) => ({
        name: prettyLabel(status),
        value: jobStats?.statusCounts?.[status] || 0,
        percentage: jobStats?.total > 0
            ? (((jobStats?.statusCounts?.[status] || 0) / jobStats.total) * 100).toFixed(1)
            : 0,
        color: STATUS_COLORS[index % STATUS_COLORS.length]
    }));

    const jobTypeData = jobStats?.typeCounts
        ? Object.entries(jobStats.typeCounts)
            .map(([type, count]) => ({
                name: prettyLabel(type).replace('And', '&'),
                value: count,
                percentage: jobStats?.total > 0 ? ((count / jobStats.total) * 100).toFixed(1) : 0
            }))
            .filter(item => item.value > 0)
        : [];

    const allDeliveryStatuses = ['not_scheduled', 'scheduled', 'in_transit', 'delivered', 'failed'];

    const deliveryStatusData = allDeliveryStatuses.map(status => ({
        name: prettyLabel(status),
        value: deliveryStats?.statusCounts?.[status] || 0
    }));

    const userRoleData = userStats?.roleCounts
        ? Object.entries(userStats.roleCounts).map(([role, count]) => ({
            name: prettyLabel(role),
            value: count
        }))
        : [];

    const calculateRadarValue = (value, max = 100) => {
        const num = parseFloat(value) || 0;
        return Math.min(Math.max(num, 0), max);
    };

    const performanceRadarData = [
        {
            metric: 'Jobs',
            value: calculateRadarValue(jobStats?.timeStats?.thisMonth || 0, 50) * 2,
            actual: jobStats?.timeStats?.thisMonth || 0
        },
        {
            metric: 'Delivery Rate',
            value: calculateRadarValue(deliveryStats?.completionRate || 0),
            actual: `${deliveryStats?.completionRate || 0}%`
        },
        {
            metric: 'Active Users',
            value: calculateRadarValue(userStats?.activePercentage || 0),
            actual: `${userStats?.activePercentage || 0}%`
        },
        {
            metric: 'Design Complete',
            value: performanceStats?.designers?.total > 0
                ? calculateRadarValue((performanceStats.designers.completed / performanceStats.designers.total) * 100)
                : 0,
            actual: performanceStats?.designers?.total > 0
                ? `${((performanceStats.designers.completed / performanceStats.designers.total) * 100).toFixed(1)}%`
                : '0%'
        },
        {
            metric: 'Estimation Done',
            value: performanceStats?.estimators?.total > 0
                ? calculateRadarValue((performanceStats.estimators.completed / performanceStats.estimators.total) * 100)
                : 0,
            actual: performanceStats?.estimators?.total > 0
                ? `${((performanceStats.estimators.completed / performanceStats.estimators.total) * 100).toFixed(1)}%`
                : '0%'
        },
        {
            metric: 'Invoiced Jobs',
            value: calculateRadarValue(jobStats?.invoiceStats?.invoicedPercentage || 0),
            actual: `${jobStats?.invoiceStats?.invoicedPercentage || 0}%`
        }
    ];

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 shadow-2xl shadow-zinc-900/10">
                    <p className="text-sm font-bold text-zinc-950">{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} className="mt-1 text-xs font-semibold" style={{ color: entry.color }}>
                            {entry.name}: {entry.value}
                        </p>
                    ))}
                </div>
            );
        }

        return null;
    };

    const groupedUsersByRole = useMemo(() => {
        const groups = {};
        const preferredRoleOrder = [
            'super_admin',
            'admin',
            'estimator',
            'designer',
            'driver',
        ];

        analyticsData.users.forEach(user => {
            const role = user.role || 'unknown';

            if (!groups[role]) {
                groups[role] = [];
            }

            groups[role].push(user);
        });

        return Object.entries(groups)
            .sort(([roleA], [roleB]) => {
                const indexA = preferredRoleOrder.indexOf(roleA);
                const indexB = preferredRoleOrder.indexOf(roleB);

                const hasPreferredA = indexA !== -1;
                const hasPreferredB = indexB !== -1;

                if (hasPreferredA && hasPreferredB) return indexA - indexB;
                if (hasPreferredA) return -1;
                if (hasPreferredB) return 1;

                return roleA.localeCompare(roleB);
            })
            .map(([role, users]) => ({
                role,
                users: users.sort((a, b) =>
                    getUserDisplayName(a).localeCompare(getUserDisplayName(b))
                ),
            }));
    }, [analyticsData.users]);

    const cardData = pendingAnalyticsData || analyticsData;

    const cardJobStats = useMemo(() => calculateJobStats(cardData.jobs), [cardData.jobs]);
    const cardUserStats = useMemo(() => calculateUserStats(cardData.users), [cardData.users]);
    const cardDeliveryStats = useMemo(() => calculateDeliveryStats(cardData.deliveries), [cardData.deliveries]);
    const cardActivityStats = useMemo(() => calculateActivityStats(cardData.actionLogs), [cardData.actionLogs]);
    const cardSmsStats = useMemo(() => calculateSMSStats(cardData.smsLogs), [cardData.smsLogs]);

    const miniCards = [
        {
            label: 'Total Jobs',
            value: cardJobStats?.total || 0,
            sub: `${cardJobStats?.timeStats?.thisWeek || 0} this week`,
            icon: FiBriefcase,
            loading: analyticsLoading && !loadedSections.jobs,
        },
        {
            label: 'Active Users',
            value: cardUserStats?.activeUsers || 0,
            sub: `${cardUserStats?.total || 0} total users`,
            icon: FiUsers,
            loading: analyticsLoading && !loadedSections.users,
        },
        {
            label: 'Deliveries',
            value: cardDeliveryStats?.completed || 0,
            sub: `${cardDeliveryStats?.completionRate || 0}% completion`,
            icon: FiTruck,
            loading: analyticsLoading && !loadedSections.deliveries,
        },
        {
            label: 'Activity',
            value: cardActivityStats?.total || 0,
            sub: `${cardActivityStats?.actionsToday || 0} today`,
            icon: FiActivity,
            loading: analyticsLoading && !loadedSections.actionLogs,
        },
        {
            label: 'Messages',
            value: cardSmsStats?.total || 0,
            sub: `${cardSmsStats?.timeStats?.thisWeek || 0} this week`,
            icon: FiMessageSquare,
            loading: analyticsLoading && !loadedSections.smsLogs,
        },
        {
            label: 'Urgent',
            value: cardJobStats?.urgentStats?.total || 0,
            sub: `${cardJobStats?.urgentStats?.quote || 0} quote · ${cardJobStats?.urgentStats?.design || 0} design`,
            icon: FiAlertCircle,
            loading: analyticsLoading && !loadedSections.jobs,
        },
        {
            label: 'Invoiced',
            value: cardJobStats?.invoiceStats?.invoiced || 0,
            sub: `${cardJobStats?.invoiceStats?.invoicedPercentage || 0}% complete`,
            icon: FiCheckCircle,
            loading: analyticsLoading && !loadedSections.jobs,
        },
        {
            label: 'Production',
            value: productionStats?.production?.started || 0,
            sub: `${productionStats?.production?.completed || 0} completed`,
            icon: FiPackage,
            loading: productionLoading,
        },
    ];

    const getLeaderboardRows = (stats, sortMode = 'done') => {
        if (!stats?.byUser) return [];

        return Object.values(stats.byUser)
            .map(values => ({
                ...values,
                name: values.name || 'Unknown',
                completionRate: values.total > 0
                    ? Math.round((values.completed / values.total) * 100)
                    : 0,
            }))
            .sort((a, b) => {
                if (sortMode === 'percentage') {
                    if (b.completionRate !== a.completionRate) {
                        return b.completionRate - a.completionRate;
                    }

                    return b.completed - a.completed;
                }

                if (b.completed !== a.completed) {
                    return b.completed - a.completed;
                }

                return b.completionRate - a.completionRate;
            });
    };

    if (initialLoading) {
        return (
            <div className="analytics-page space-y-6">
                <div className="rounded-[2rem] border border-zinc-200 bg-white p-6 shadow-sm">
                    <Skeleton width={180} height={18} className="mb-4" />
                    <Skeleton width={360} height={42} className="mb-3" />
                    <Skeleton width={520} height={18} />
                </div>

                <div className="analytics-stats grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {[...Array(8)].map((_, i) => (
                        <div key={i} className="rounded-[1.5rem] border border-zinc-200 bg-white p-5 shadow-sm">
                            <Skeleton width={42} height={42} borderRadius={16} className="mb-5" />
                            <Skeleton width={90} height={14} className="mb-2" />
                            <Skeleton width={120} height={36} className="mb-3" />
                            <Skeleton width={150} height={12} />
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="rounded-[1.75rem] border border-zinc-200 bg-white p-5 shadow-sm">
                            <Skeleton width={180} height={20} className="mb-5" />
                            <Skeleton height={300} borderRadius={20} />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    const StatCard = ({ item }) => {
        const Icon = item.icon;

        return (
            <div className="analytics-stat group rounded-[1.5rem] border border-zinc-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-zinc-900/5 dark:border-zinc-800 dark:bg-zinc-950">
                {item.loading ? (
                    <>
                        <Skeleton width={42} height={42} borderRadius={16} className="mb-5" />
                        <Skeleton width={90} height={14} className="mb-2" />
                        <Skeleton width={100} height={34} className="mb-3" />
                        <Skeleton width={130} height={12} />
                    </>
                ) : (
                    <>
                        <div className="mb-5 flex items-center justify-between">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-zinc-950 text-white shadow-lg shadow-zinc-900/10 dark:bg-white dark:text-zinc-950">
                                <Icon className="h-5 w-5" />
                            </div>
                            <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.18em] ${item.loading
                                ? 'border-amber-200 bg-amber-50 text-amber-700'
                                : 'border-zinc-200 bg-zinc-50 text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400'
                                }`}>
                                {item.loading && <FiRefreshCw className="h-3 w-3 animate-spin" />}
                                {item.loading ? 'Updating' : 'Live'}
                            </div>
                        </div>

                        <p className="text-sm font-semibold text-zinc-500 dark:text-zinc-400">{item.label}</p>
                        <p className="mt-1 text-4xl font-bold tracking-tight text-zinc-950 dark:text-white">
                            {item.value}
                        </p>
                        <p className="mt-3 text-xs font-semibold text-zinc-500 dark:text-zinc-500">
                            {item.sub}
                        </p>
                    </>
                )}
            </div>
        );
    };

    const ChartCard = ({ icon: Icon, title, subtitle, loading, children, footer }) => (
        <div className="analytics-chart relative overflow-hidden rounded-[1.75rem] border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
            <div className="mb-5 flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-zinc-100 text-zinc-950 dark:bg-zinc-900 dark:text-white">
                        <Icon className="h-5 w-5" />
                    </div>
                    <div>
                        <h3 className="text-base font-bold tracking-tight text-zinc-950 dark:text-white">
                            {title}
                        </h3>
                        {subtitle && (
                            <p className="mt-0.5 text-xs font-semibold text-zinc-500">
                                {subtitle}
                            </p>
                        )}
                    </div>
                </div>

                {loading && (
                    <div className="inline-flex items-center gap-2 rounded-full bg-zinc-100 px-3 py-1.5 text-xs font-bold text-zinc-500 dark:bg-zinc-900">
                        <FiRefreshCw className="h-3.5 w-3.5 animate-spin" />
                        Updating
                    </div>
                )}
            </div>

            <div className={loading ? 'opacity-50 transition-opacity' : 'opacity-100 transition-opacity'}>
                {children}
                {footer}
            </div>
        </div>
    );

    const Leaderboard = ({
        title,
        icon: Icon,
        stats,
        loading,
        sortMode,
        onSortModeChange,
        leaderboardKey,
    }) => {
        const rows = getLeaderboardRows(stats, sortMode);
        const expanded = !!expandedLeaderboards[leaderboardKey];

        const [first, second, third, ...rest] = rows;
        const visibleRest = expanded ? rest : rest.slice(0, 5);
        const hiddenCount = Math.max(rest.length - 5, 0);

        return (
            <div className="rounded-[1.75rem] border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
                <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-zinc-950 text-white dark:bg-white dark:text-zinc-950">
                            <Icon className="h-5 w-5" />
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-zinc-950 dark:text-white">{title}</h3>
                            <p className="text-xs font-semibold text-zinc-500">
                                Ranked by selected performance mode
                            </p>
                        </div>
                    </div>

                    <div className="flex rounded-full bg-zinc-100 p-1 dark:bg-zinc-900">
                        {[
                            { key: 'done', label: 'Done count' },
                            { key: 'percentage', label: 'Completion %' },
                        ].map(option => (
                            <button
                                key={option.key}
                                type="button"
                                onClick={() => onSortModeChange(option.key)}
                                className={`rounded-full px-3 py-1.5 text-xs font-bold transition ${sortMode === option.key
                                    ? 'bg-zinc-950 text-white shadow-sm dark:bg-white dark:text-zinc-950'
                                    : 'text-zinc-500 hover:text-zinc-950 dark:hover:text-white'
                                    }`}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <Skeleton height={310} borderRadius={22} />
                ) : rows.length === 0 ? (
                    <div className="flex h-64 items-center justify-center rounded-[1.25rem] border border-dashed border-zinc-200 bg-zinc-50 text-sm font-semibold text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/40">
                        No performance data
                    </div>
                ) : (
                    <>
                        <div className="analytics-podium mb-5 grid grid-cols-3 items-end gap-3">
                            {second && <PodiumCard rank={2} row={second} compact />}
                            {first && <PodiumCard rank={1} row={first} winner />}
                            {third && <PodiumCard rank={3} row={third} compact />}
                        </div>

                        <div className="space-y-2 overflow-hidden transition-all duration-500 ease-out">
                            {visibleRest.map((row, index) => (
                                <div
                                    key={row.id || row.name}
                                    className={`analytics-ranking-row flex items-center justify-between rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900/40 ${expanded ? 'analytics-slide-in' : ''
                                        }`}
                                    style={expanded ? { animationDelay: `${index * 45}ms` } : undefined}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-sm font-bold text-zinc-950 shadow-sm dark:bg-zinc-950 dark:text-white">
                                            #{index + 4}
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-zinc-950 dark:text-white">{row.name}</p>
                                            <p className="text-xs font-semibold text-zinc-500">
                                                {row.completed}/{row.total} completed
                                            </p>
                                        </div>
                                    </div>

                                    <div className="text-right">
                                        <p className="text-sm font-bold text-zinc-950 dark:text-white">
                                            {row.completionRate}%
                                        </p>
                                        <div className="mt-1 h-1.5 w-20 overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-800">
                                            <div
                                                className="h-full rounded-full bg-zinc-950 dark:bg-white"
                                                style={{ width: `${row.completionRate}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {hiddenCount > 0 && (
                            <button
                                type="button"
                                onClick={() => {
                                    setExpandedLeaderboards(prev => ({
                                        ...prev,
                                        [leaderboardKey]: !expanded,
                                    }));
                                }}
                                className="mt-4 w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm font-bold text-zinc-700 transition hover:border-zinc-950 hover:bg-zinc-950 hover:text-white dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-white dark:hover:text-zinc-950"
                            >
                                {expanded ? 'Show less' : `Show ${hiddenCount} more`}
                            </button>
                        )}
                    </>
                )}
            </div>
        );
    };

    const PodiumCard = ({ row, rank, winner, compact }) => (
        <div
            className={[
                `analytics-podium-card analytics-rank-${rank} rounded-[1.35rem] border text-center shadow-sm relative`,
                winner
                    ? 'border-zinc-950 bg-zinc-950 px-4 py-6 text-white shadow-xl shadow-zinc-900/15 dark:border-white dark:bg-white dark:text-zinc-950'
                    : 'border-zinc-200 bg-zinc-50 px-3 py-4 text-zinc-950 dark:border-zinc-800 dark:bg-zinc-900 dark:text-white',
                compact ? 'mb-2' : ''
            ].join(' ')}
        >
            <Crown className={`absolute ${rank === 1 ? "text-yellow-500 w-12 h-12" : rank === 2 ? "text-gray-500 w-8 h-8" : "text-amber-700 w-4 h-4"}`} />
            <div
                className={[
                    'mx-auto mb-3 flex items-center justify-center rounded-full font-bold',
                    winner
                        ? 'h-14 w-14 bg-white text-2xl text-zinc-950 dark:bg-zinc-950 dark:text-white'
                        : 'h-11 w-11 bg-white text-lg text-zinc-950 shadow-sm dark:bg-zinc-950 dark:text-white'
                ].join(' ')}
            >
                #{rank}
            </div>

            <p className={winner ? 'text-sm font-bold' : 'text-xs font-bold'}>
                {row.name}
            </p>

            <p className={winner ? 'mt-2 text-3xl font-bold' : 'mt-2 text-2xl font-bold'}>
                {row.completed}
            </p>

            <div>
                <p className={winner ? 'text-xs font-bold opacity-70' : 'text-[11px] font-bold text-zinc-500'}>
                    {row.completionRate}% complete
                </p>
                <p className={winner ? 'text-xs font-bold opacity-70' : 'text-[11px] font-bold text-zinc-500'}>
                    {row.total} total
                </p>
            </div>
        </div>
    );

    const userRoleDisplay = (userRole = "Unknown") => {
        return userRole
            ?.split("_")
            ?.join(" ")
            ?.split(" ")
            ?.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            ?.join(" ");
    };



    return (
        <div className="analytics-page space-y-6 pb-10">
            <div className="analytics-header rounded-[2rem] border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 sm:p-7">
                <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
                    <div>
                        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400">
                            <FiBarChart2 className="h-3.5 w-3.5" />
                            Analytics
                        </div>

                        <h1 className="text-4xl font-bold tracking-[-0.045em] text-zinc-950 dark:text-white sm:text-5xl">
                            Company insights
                        </h1>

                        <p className="mt-3 max-w-2xl text-sm font-medium leading-6 text-zinc-500">
                            Clean overview of jobs, activity, delivery performance and team output for{' '}
                            <span className="font-bold text-zinc-950 dark:text-white">{company?.name}</span>.
                        </p>
                    </div>

                    <div className="flex flex-col gap-2 text-sm xl:text-right">
                        <button
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-zinc-950 px-5 py-3 text-sm font-bold text-white transition hover:bg-zinc-800 disabled:opacity-50 dark:bg-white dark:text-zinc-950"
                        >
                            <FiRefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>

                        {lastUpdated && (
                            <div className="flex items-center justify-center gap-2 text-xs font-semibold text-zinc-500 xl:justify-end">
                                <FiClock className="h-3.5 w-3.5" />
                                Last updated: {formatDateTime(lastUpdated)}
                            </div>
                        )}
                    </div>
                </div>
            </div>


            {/* Filters */}
            <div className="analytics-filters rounded-[1.75rem] border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
                <div className="flex flex-col gap-4 2xl:flex-row 2xl:items-center 2xl:justify-between">
                    <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900/50">
                            <label className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                                <FiCalendar className="h-3.5 w-3.5" />
                                Date range
                            </label>

                            <div className="flex items-center gap-2">
                                <DatePicker
                                    selected={startDate}
                                    onChange={handleDateChange}
                                    startDate={startDate}
                                    endDate={endDate}
                                    selectsRange
                                    isClearable={false}
                                    shouldCloseOnSelect={false}
                                    placeholderText="Select one day or range"
                                    className="w-full bg-transparent text-sm font-bold text-zinc-950 outline-none placeholder:text-zinc-400 dark:text-white"
                                    dateFormat="dd/MM/yyyy"
                                    monthsShown={1}
                                    popperPlacement="bottom-start"
                                    popperClassName="date-picker-popper"
                                />

                                {(startDate || endDate) && (
                                    <button
                                        type="button"
                                        onClick={clearDateFilter}
                                        aria-label="Clear date range"
                                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-zinc-500 shadow-sm transition hover:text-zinc-950 dark:bg-zinc-950 dark:hover:text-white"
                                    >
                                        <FiX className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="rounded-2xl border border-zinc-200 bg-zinc-50 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900/50">
                            <label className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-zinc-500">
                                <FiUser className="h-3.5 w-3.5" />
                                User
                            </label>

                            <div className="relative">
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setUserDropdownOpen(prev => !prev)}
                                        aria-expanded={userDropdownOpen}
                                        aria-label="Filter by user"
                                        className="flex w-full items-center justify-between gap-3 rounded-xl bg-transparent text-left text-sm font-bold text-zinc-950 outline-none dark:text-white"
                                    >
                                        <span className="truncate">
                                            {selectedUserId === 'all'
                                                ? 'All users'
                                                : getUserDisplayName(analyticsData.users.find(user => user.id === selectedUserId))}
                                        </span>
                                        <FiChevronDown className={`h-4 w-4 text-zinc-400 transition ${userDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>

                                    {userDropdownOpen && (
                                        <div className="absolute left-0 top-full z-50 mt-3 max-h-72 w-full overflow-auto rounded-2xl border border-zinc-200 bg-white p-2 shadow-2xl shadow-zinc-900/15 dark:border-zinc-800 dark:bg-zinc-950">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedUserId('all');
                                                    setUserDropdownOpen(false);
                                                }}
                                                className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm font-bold transition ${selectedUserId === 'all'
                                                    ? 'bg-zinc-950 text-white dark:bg-white dark:text-zinc-950'
                                                    : 'text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-900'
                                                    }`}
                                            >
                                                All users
                                                {selectedUserId === 'all' && <FiCheckCircle className="h-4 w-4" />}
                                            </button>

                                            {groupedUsersByRole.map(group => (
                                                <div key={group.role} className="mt-2 first:mt-1">
                                                    <div className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
                                                        {userRoleDisplay(group.role)}
                                                    </div>

                                                    {group.users.map(user => {
                                                        const active = selectedUserId === user.id;

                                                        return (
                                                            <button
                                                                key={user.id}
                                                                type="button"
                                                                onClick={() => {
                                                                    setSelectedUserId(user.id);
                                                                    setUserDropdownOpen(false);
                                                                }}
                                                                className={`mt-1 flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-left transition ${active
                                                                    ? 'bg-zinc-950 text-white dark:bg-white dark:text-zinc-950'
                                                                    : 'text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-900'
                                                                    }`}
                                                            >
                                                                <span className="min-w-0">
                                                                    <span className="block truncate text-sm font-bold">
                                                                        {getUserDisplayName(user)}
                                                                    </span>
                                                                    <span className={`block truncate text-xs font-semibold ${active ? 'opacity-70' : 'text-zinc-400'}`}>
                                                                        {user.email || 'Team member'}
                                                                    </span>
                                                                </span>

                                                                {active && <FiCheckCircle className="h-4 w-4 shrink-0" />}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <FiChevronDown className="pointer-events-none absolute right-0 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                            </div>
                        </div>
                    </div>

                    <div className="analytics-ranges flex flex-wrap items-center gap-2 2xl:justify-end">
                        {[
                            { label: 'Last month', months: 1 },
                            { label: 'Last 3 months', months: 3 },
                            { label: 'Last 6 months', months: 6 },
                        ].map(item => (
                            <button
                                key={item.label}
                                type="button"
                                onClick={() => setQuickRange(item.months)}
                                className="rounded-full border border-zinc-200 bg-white px-4 py-2 text-xs font-bold text-zinc-700 shadow-sm transition hover:border-zinc-950 hover:bg-zinc-950 hover:text-white dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-white dark:hover:text-zinc-950"
                            >
                                {item.label}
                            </button>
                        ))}

                        {(startDate || endDate || selectedUserId !== 'all') && (
                            <button
                                type="button"
                                onClick={clearAllFilters}
                                className="rounded-full bg-zinc-100 px-4 py-2 text-xs font-bold text-zinc-500 transition hover:bg-zinc-200 hover:text-zinc-950 dark:bg-zinc-900 dark:hover:bg-zinc-800 dark:hover:text-white"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2 text-xs font-bold text-zinc-500">
                    <div className="inline-flex items-center gap-2 rounded-full bg-zinc-100 px-3 py-1.5 dark:bg-zinc-900">
                        <FiFilter className="h-3.5 w-3.5" />
                        {formatDateLabel()}
                    </div>

                    <div className="inline-flex items-center gap-2 rounded-full bg-zinc-100 px-3 py-1.5 dark:bg-zinc-900">
                        <FiUser className="h-3.5 w-3.5" />
                        {selectedUserId === 'all'
                            ? 'All users'
                            : getUserDisplayName(analyticsData.users.find(user => user.id === selectedUserId))}
                    </div>

                    {analyticsLoading && (
                        <div className="inline-flex items-center gap-2 rounded-full bg-zinc-950 px-3 py-1.5 text-white dark:bg-white dark:text-zinc-950">
                            <FiRefreshCw className="h-3.5 w-3.5 animate-spin" />
                            Updating analytics
                        </div>
                    )}
                </div>
            </div>

            {/* Overview Cards */}
            <div className="analytics-stats grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {miniCards.map(item => (
                    <StatCard key={item.label} item={item} />
                ))}
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <ChartCard
                    icon={FiPieChart}
                    title="Job Status"
                    subtitle={`${jobStats?.total || 0} total jobs`}
                    loading={analyticsLoading}
                    footer={
                        <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3">
                            {jobStatusData.map(item => (
                                <div key={item.name} className="rounded-2xl bg-zinc-50 px-3 py-2 dark:bg-zinc-900/50">
                                    <div className="flex items-center gap-1">
                                        <span
                                            className="h-2.5 w-2.5 rounded-full"
                                            style={{ backgroundColor: item.color }}
                                        />
                                        <span className="truncate text-xs font-bold text-zinc-600 dark:text-zinc-400">
                                            {item.name}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-lg font-bold text-zinc-950 dark:text-white">
                                        {item.value}
                                    </p>
                                </div>
                            ))}
                        </div>
                    }
                >
                    {jobStatusData.filter(item => item.value > 0).length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                                <Pie
                                    data={jobStatusData.filter(item => item.value > 0)}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={62}
                                    outerRadius={105}
                                    paddingAngle={1}
                                    dataKey="value"
                                    labelLine={false}
                                    label={({ percentage }) => percentage > 7 ? `${percentage}%` : ''}
                                >
                                    {jobStatusData.filter(item => item.value > 0).map(entry => (
                                        <Cell key={entry.name} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex h-[300px] items-center justify-center rounded-[1.25rem] bg-zinc-50 text-sm font-bold text-zinc-500 dark:bg-zinc-900/50">
                            No job status data
                        </div>
                    )}
                </ChartCard>

                <ChartCard
                    icon={FiTarget}
                    title="Job Types"
                    subtitle="Distribution by job type"
                    loading={analyticsLoading}
                    footer={
                        <div className="mt-5 grid grid-cols-2 gap-2">
                            {jobTypeData.map(item => (
                                <div key={item.name} className="rounded-2xl bg-zinc-50 px-3 py-2 dark:bg-zinc-900/50">
                                    <div className="flex items-center justify-between gap-3">
                                        <span className="text-sm font-bold text-zinc-700 dark:text-zinc-300">{item.name}</span>
                                        <span className="text-xl font-bold text-zinc-950 dark:text-white">{item.value}</span>
                                    </div>
                                    <p className="mt-1 text-xs font-semibold text-zinc-500">{item.percentage}% of jobs</p>
                                </div>
                            ))}
                        </div>
                    }
                >
                    {jobTypeData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={jobTypeData} margin={{ top: 15, right: 20, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                                <XAxis dataKey="name" tick={{ fill: '#71717A', fontSize: 11, fontWeight: 700 }} tickLine={false} axisLine={false} />
                                <YAxis tick={{ fill: '#71717A', fontSize: 11, fontWeight: 700 }} tickLine={false} axisLine={false} allowDecimals={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="value" fill={COLORS.black} radius={[12, 12, 0, 0]} name="Jobs" />
                            </BarChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex h-[300px] items-center justify-center rounded-[1.25rem] bg-zinc-50 text-sm font-bold text-zinc-500 dark:bg-zinc-900/50">
                            No job type data
                        </div>
                    )}
                </ChartCard>

                <ChartCard
                    icon={FiTrendingUp}
                    title="Job Creation Trend"
                    subtitle="Last 30 days"
                    loading={analyticsLoading}
                >
                    <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={jobTrends} margin={{ top: 15, right: 20, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="analyticsJobsGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={COLORS.black} stopOpacity={0.18} />
                                    <stop offset="95%" stopColor={COLORS.black} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                            <XAxis dataKey="date" interval={isMobile ? 'preserveStartEnd' : 4} minTickGap={isMobile ? 30 : 5} tick={{ fill: '#71717A', fontSize: 11, fontWeight: 700 }} tickLine={false} axisLine={false} />
                            <YAxis tick={{ fill: '#71717A', fontSize: 11, fontWeight: 700 }} tickLine={false} axisLine={false} allowDecimals={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Area
                                type="monotone"
                                dataKey="count"
                                stroke={COLORS.black}
                                strokeWidth={3}
                                fill="url(#analyticsJobsGradient)"
                                name="Jobs Created"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard
                    icon={FiActivity}
                    title="Activity Trend"
                    subtitle="Last 30 days"
                    loading={analyticsLoading}
                >
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={activityTrends} margin={{ top: 15, right: 20, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                            <XAxis dataKey="date" interval={isMobile ? 'preserveStartEnd' : 4} minTickGap={isMobile ? 30 : 5} tick={{ fill: '#71717A', fontSize: 11, fontWeight: 700 }} tickLine={false} axisLine={false} />
                            <YAxis tick={{ fill: '#71717A', fontSize: 11, fontWeight: 700 }} tickLine={false} axisLine={false} allowDecimals={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Line
                                type="monotone"
                                dataKey="count"
                                stroke={COLORS.black}
                                strokeWidth={3}
                                dot={isMobile ? false : { fill: COLORS.black, r: 4 }}
                                activeDot={{ r: 7 }}
                                name="Actions"
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>




            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <ChartCard
                    icon={FiTruck}
                    title="Delivery Status"
                    subtitle={`${deliveryStats?.total || 0} total deliveries`}
                    loading={analyticsLoading}
                    footer={
                        <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3">
                            {deliveryStatusData.map((item, index) => (
                                <div
                                    key={item.name}
                                    className="rounded-2xl border border-zinc-200 bg-zinc-50 px-3 py-2 dark:border-zinc-800 dark:bg-zinc-900/50"
                                >
                                    <div className="flex items-center gap-2">
                                        <span
                                            className="h-2.5 w-2.5 rounded-full"
                                            style={{ backgroundColor: STATUS_COLORS[index % STATUS_COLORS.length] }}
                                        />
                                        <span className="truncate text-xs font-bold text-zinc-600 dark:text-zinc-400">
                                            {item.name}
                                        </span>
                                    </div>
                                    <p className="mt-1 text-lg font-bold text-zinc-950 dark:text-white">
                                        {item.value}
                                    </p>
                                </div>
                            ))}
                        </div>
                    }
                >
                    {deliveryStatusData.filter(item => item.value > 0).length > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                            <PieChart>
                                <Pie
                                    data={deliveryStatusData.filter(item => item.value > 0)}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={58}
                                    outerRadius={98}
                                    paddingAngle={1}
                                    dataKey="value"
                                >
                                    {deliveryStatusData.filter(item => item.value > 0).map((entry, index) => (
                                        <Cell
                                            key={entry.name}
                                            fill={STATUS_COLORS[index % STATUS_COLORS.length]}
                                        />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex h-[280px] items-center justify-center rounded-[1.25rem] bg-zinc-50 text-sm font-bold text-zinc-500 dark:bg-zinc-900/50">
                            No delivery data
                        </div>
                    )}
                </ChartCard>

                <ChartCard
                    icon={FiUsers}
                    title="User Roles"
                    subtitle={`${userStats?.total || 0} team members`}
                    loading={analyticsLoading}
                >
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={userRoleData} margin={{ top: 15, right: 20, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                            <XAxis dataKey="name" tick={{ fill: '#71717A', fontSize: 11, fontWeight: 700 }} tickLine={false} axisLine={false} />
                            <YAxis tick={{ fill: '#71717A', fontSize: 11, fontWeight: 700 }} tickLine={false} axisLine={false} allowDecimals={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="value" fill={COLORS.black} radius={[12, 12, 0, 0]} name="Users" />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            <ChartCard
                icon={FiLayers}
                title="Overall Performance"
                subtitle="Operational health overview"
                loading={analyticsLoading || performanceLoading || productionLoading}
                footer={
                    <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3">
                        {performanceRadarData.map(item => (
                            <div key={item.metric} className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4 text-center dark:border-zinc-800 dark:bg-zinc-900/50">
                                <p className="text-xs font-bold text-zinc-500">{item.metric}</p>
                                <p className="mt-1 text-xl font-bold text-zinc-950 dark:text-white">{item.actual}</p>
                            </div>
                        ))}
                    </div>
                }
            >
                <ResponsiveContainer width="100%" height={isMobile ? 260 : 420}>
                    <RadarChart data={performanceRadarData} margin={isMobile ? { top: 30, right: 45, bottom: 30, left: 45 } : { top: 20, right: 80, bottom: 20, left: 80 }}>
                        <PolarGrid stroke="#D4D4D8" />
                        <PolarAngleAxis dataKey="metric" tick={{ fill: '#71717A', fontSize: isMobile ? 10 : 12, fontWeight: 800 }} tickFormatter={value => isMobile ? ({ 'Delivery Rate': 'Delivery', 'Active Users': 'Users', 'Design Complete': 'Design', 'Estimation Done': 'Estimation', 'Invoiced Jobs': 'Invoiced' }[value] || value) : value} />
                        <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#71717A', fontSize: 10 }} />
                        <Radar
                            name="Performance"
                            dataKey="value"
                            stroke={COLORS.black}
                            fill={COLORS.black}
                            fillOpacity={0.18}
                            strokeWidth={3}
                        />
                        <Tooltip
                            content={({ active, payload }) => {
                                if (active && payload && payload.length) {
                                    return (
                                        <div className="rounded-2xl border border-zinc-200 bg-white px-4 py-3 shadow-2xl shadow-zinc-900/10">
                                            <p className="text-sm font-bold text-zinc-950">{payload[0].payload.metric}</p>
                                            <p className="mt-1 text-xs font-bold text-zinc-500">
                                                Value: {payload[0].payload.actual}
                                            </p>
                                        </div>
                                    );
                                }

                                return null;
                            }}
                        />
                    </RadarChart>
                </ResponsiveContainer>
            </ChartCard>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <Leaderboard
                    title="Designer Performance"
                    icon={FiAward}
                    stats={performanceStats?.designers}
                    loading={performanceLoading}
                    sortMode={designerSortMode}
                    onSortModeChange={setDesignerSortMode}
                    leaderboardKey="designers"
                />

                <Leaderboard
                    title="Estimator Performance"
                    icon={FiZap}
                    stats={performanceStats?.estimators}
                    loading={performanceLoading}
                    sortMode={estimatorSortMode}
                    onSortModeChange={setEstimatorSortMode}
                    leaderboardKey="estimators"
                />
            </div>

            <div className="rounded-[1.75rem] border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
                <div className="mb-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-zinc-950 text-white dark:bg-white dark:text-zinc-950">
                            <FiUsers className="h-5 w-5" />
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-zinc-950 dark:text-white">Most Active Users</h3>
                            <p className="text-xs font-semibold text-zinc-500">Based on activity logs in selected period</p>
                        </div>
                    </div>
                </div>

                {analyticsLoading ? (
                    <Skeleton height={180} borderRadius={22} />
                ) : activityStats?.topUsers?.length > 0 ? (
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {activityStats.topUsers.map((user, index) => (
                            <div
                                key={`${user.name}-${index}`}
                                className="flex items-center justify-between rounded-[1.25rem] border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-900/50"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-sm font-bold text-zinc-950 shadow-sm dark:bg-zinc-950 dark:text-white">
                                        #{index + 1}
                                    </div>

                                    <div>
                                        <p className="text-sm font-bold text-zinc-950 dark:text-white">{user.name}</p>
                                        <p className="text-xs font-bold text-zinc-500">
                                            {userRoleDisplay(user.role)} · {user.count} actions
                                        </p>
                                    </div>
                                </div>

                                <p className="text-2xl font-bold text-zinc-950 dark:text-white">{user.count}</p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex h-40 items-center justify-center rounded-[1.25rem] border border-dashed border-zinc-200 bg-zinc-50 text-sm font-bold text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/50">
                        No activity data
                    </div>
                )}
            </div>

            <div className="rounded-[1.75rem] border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
                <div className="mb-5 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-zinc-100 text-zinc-950 dark:bg-zinc-900 dark:text-white">
                        <FiCalendar className="h-5 w-5" />
                    </div>
                    <div>
                        <h3 className="text-base font-bold text-zinc-950 dark:text-white">Quick Summary</h3>
                        <p className="text-xs font-semibold text-zinc-500">Fast numbers for the selected filter</p>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                    {[
                        ['Jobs Today', jobStats?.timeStats?.today || 0],
                        ['Jobs This Month', jobStats?.timeStats?.thisMonth || 0],
                        ['Quote Sent', jobStats?.quoteSent || 0],
                        ['Site Measure', jobStats?.siteMeasureRequired || 0],
                        ['SMS This Month', smsStats?.timeStats?.thisMonth || 0],
                        ['Actions This Week', activityStats?.actionsThisWeek || 0],
                    ].map(([label, value]) => (
                        <div key={label} className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4 dark:border-zinc-800 dark:bg-zinc-900/50">
                            <p className="text-xs font-bold text-zinc-500">{label}</p>
                            <p className="mt-2 text-3xl font-bold tracking-tight text-zinc-950 dark:text-white">{value}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="rounded-[1.75rem] border border-zinc-200 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950">
                <div className="mb-5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-zinc-950 text-white dark:bg-white dark:text-zinc-950">
                            <FiActivity className="h-5 w-5" />
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-zinc-950 dark:text-white">Latest Activity</h3>
                            <p className="text-xs font-semibold text-zinc-500">Last 8 actions in selected period</p>
                        </div>
                    </div>
                </div>

                {analyticsLoading ? (
                    <Skeleton height={320} borderRadius={22} />
                ) : latestLogs.length > 0 ? (
                    <div className="space-y-2">
                        {latestLogs.map((log, index) => {
                            const ActionIcon = getActionIcon(log.action);
                            const actionColor = getActionColor(log.action);

                            return (
                                <div
                                    key={log.id || index}
                                    className="flex flex-col gap-3 rounded-[1.25rem] border border-zinc-200 bg-zinc-50 p-4 transition hover:bg-zinc-100 dark:border-zinc-800 dark:bg-zinc-900/50 dark:hover:bg-zinc-900 sm:flex-row sm:items-start"
                                >
                                    <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border ${actionColor}`}>
                                        <ActionIcon className="h-5 w-5" />
                                    </div>

                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                                            <div className="min-w-0">
                                                <p className="text-sm font-bold text-zinc-950 dark:text-white">
                                                    {prettyLabel(log.action)}
                                                </p>
                                                <p className="mt-1 line-clamp-2 text-xs font-semibold leading-5 text-zinc-500">
                                                    {parseLogDetails(log.details)}
                                                </p>
                                            </div>

                                            <span className="shrink-0 text-xs font-bold text-zinc-400">
                                                {formatDateTime(log.timestamp)}
                                            </span>
                                        </div>

                                        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs font-bold text-zinc-500">
                                            {log.users && (
                                                <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 dark:bg-zinc-950">
                                                    <FiUser className="h-3 w-3" />
                                                    {log.users.username || log.users.full_name || 'Unknown User'}
                                                </span>
                                            )}

                                            {log.jobs && (
                                                <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 font-mono dark:bg-zinc-950">
                                                    <FiBriefcase className="h-3 w-3" />
                                                    {log.jobs.unique_id}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="flex h-48 items-center justify-center rounded-[1.25rem] border border-dashed border-zinc-200 bg-zinc-50 text-sm font-bold text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/50">
                        No activity logs available
                    </div>
                )}
            </div>
        </div>
    );
};

export default Analytics;